import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { PrevBtn, NextBtn, PaginationWrapper } from "../Styles/PageStyles";
import Images from "../../Images";
import tw from "tailwind-styled-components";
import Config, { moveOutReasons, PaginationLimit } from "../../Config";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { truncateText } from "../../helpers";
import moment from "moment";
import { MdOutlineChevronRight } from "react-icons/md";

const Table = ({ apiData, selectedData, setSelectedData, setIsReqRej, setInitiateEviction, setIsViewReq }) => {
    const navigate = useNavigate();
    const [selectedRow, setSelectedRow] = useState(null);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
    const [openActionMenu, setOpenActionMenu] = useState(null);

    const toggleRow = (rowIndex) => {
        setSelectedRow((prev) => (prev === rowIndex ? null : rowIndex));
    };

    const typeStyles = {
        2: "bg-orange-50 text-orange-700", // Move Out
        3: "bg-red-50 text-red-700",       // Refund
        4: "bg-purple-50 text-purple-700", // Guest
        default: "bg-blue-50 text-blue-700" // Onboarding
    };

    const filteredApiData = useMemo(
        () => apiData?.filter((item) => item.type === Config.REQUEST_TYPE.ONBOARDING || item.type === Config.REQUEST_TYPE.MOVEOUT || item.type === Config.REQUEST_TYPE.REFUND) || [],
        [apiData]
    );

    const request = (item) => {
        setIsViewReq(true);
        setSelectedData(item)
        // setOpenActionMenu(null);
    };
    const ActionBtns = useCallback((item) => {
        return (
            <div className="flex gap-4 ">
                <button
                    onClick={() => request(item)}
                    className={`cursor-pointer text-sm px-4 py-1 rounded-lg ${' text-gray-600'} flex items-center gap-1`}
                >
                    <MdOutlineChevronRight className="text-gray-300" size={24} />
                </button>
            </div>
        );
    }, []);

    const goToDues = (clickOn, item) => {
        const state = { tabName: clickOn, tabKey: "totalDues" };
        if (item.type === 2) {
            navigate(`/tenantdetails/l/${item.tenantId}`, { state });
        } else if (item.type === 3) {
            navigate(`/tenantdetails/l/${item.tenantId}/1`, { state });
        }
    };

    const getName = (item) => {
        return (
            <UserImageWrapper onClick={() => goToDues('Tenant Info', item)}>
                <div className="flex-row flex">
                    <UserImage
                        src={item.profilePicture ? item.profilePicture : Images.Avatar}
                        alt={`Avatar for ${item.tenantName}`}
                    />
                </div>
                <div className="flex flex-col gap-1">
                    <span className="text-gray-600 text-sm font-medium">
                        {item?.tenantName?.charAt(0).toUpperCase() + item?.tenantName?.slice(1)}{" "}
                    </span>
                    {item.tenantMobile && (
                        <span className="text-gray-400 text-xs">#{item.tenantMobile}</span>
                    )}
                </div>
            </UserImageWrapper>
        );
    };

    const getTypeDisplay = (item) => {
        return (
            <span
                className={`px-3 py-1 rounded-full text-xs font-medium ${typeStyles[item?.type] || typeStyles.default
                    }`}
            >
                {item?.type === 2 ? 'Move Out' : item?.type === 3 ? 'Refund' : item?.type === 4 ? 'Guest' : 'Onboarding'}
            </span>
        );
    };

    const getReasonDate = (item) => {

        const reasonObj = moveOutReasons.find(reason => reason.value === item.moveOutReason);
        const displayReason = reasonObj ? reasonObj.label : item.moveOutReason;

        return (
            <div className="flex flex-col">
                {/* <span className="text-gray-600 text-sm font-medium">{displayReason}</span> */}
                {item?.type === 2 ?
                    <span className="text-gray-500 text-sm">
                        {moment(item.moveOutDate).format("DD, MMM YY")}
                    </span>
                    : <span className="text-gray-500 text-sm">
                        {moment(item.createdAt).format("DD, MMM YY")}

                    </span>}
            </div>
        );
    };

    const tableData = useMemo(
        () =>
            apiData.map((item) => ({
                name: getName(item),
                for: (
                    <div className="flex flex-col gap-0.5">
                        <span className="text-gray-700 text-sm">
                            {item.roomNum}, {item.flatName}, {item.propertyName}
                        </span>

                        {item.totalDues > 0 && (
                            <span className="text-xs text-red-600 font-medium">
                                Dues Pending
                            </span>
                        )}
                    </div>
                ),
                type: getTypeDisplay(item),
                moveOutReason: getReasonDate(item),
                description: item.description || "No description",
                action: ActionBtns(item),
            })),
        [apiData, ActionBtns]
    );

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                !event.target.closest("#menuOptions") &&
                !event.target.closest(".action-btn")
            ) {
                setOpenActionMenu(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        const handleResize = () => setIsMobileView(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const columns = useMemo(() => {
        if (isMobileView) {
            return [
                { Header: "Name", accessor: "name" },
                { Header: "Type", accessor: "type" },
            ];
        }
        return [
            { Header: "Name", accessor: "name" },
            { Header: "Property", accessor: "for" },
            { Header: "Type", accessor: "type" },
            { Header: "Date", accessor: "moveOutReason" },
            { Header: "", accessor: "action" },
        ];
    }, [isMobileView]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        pageOptions,
        page,
        state: { pageIndex, pageSize },
        previousPage,
        nextPage,
        setPageSize,
        canPreviousPage,
        canNextPage,
    } = useTable(
        {
            data: tableData,
            columns,
            initialState: { pageIndex: 0, pageSize: PaginationLimit },
        },
        useSortBy,
        usePagination
    );

    return (
        <>
            <CustomTable {...getTableProps()}>
                <Thead>
                    {headerGroups.map((headerGroup) => (
                        <Tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <Th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                    {column.render("Header")}
                                    <span>
                                        {column.isSorted ? (
                                            <img
                                                src={Images.Arrow}
                                                alt="down arrow"
                                                className={`${column.isSortedDesc ? "-rotate-90" : "rotate-90"
                                                    } w-1.5 inline-block ml-1.5`}
                                            />
                                        ) : (
                                            ""
                                        )}
                                    </span>
                                </Th>
                            ))}
                        </Tr>
                    ))}
                </Thead>

                <Tbody {...getTableBodyProps()} hasdata={(apiData.length > 0).toString()}>
                    {page.map((row, localRowIdx) => {
                        prepareRow(row);
                        const globalIndex = row.index;

                        return (
                            <React.Fragment key={row.id}>
                                <Tr
                                    {...row.getRowProps({ style: { height: "80px" } })}
                                    hasdata={(apiData.length > 0).toString()}
                                >
                                    {row.cells.map((cell, cellIndex) => (
                                        <Td
                                            {...cell.getCellProps()}
                                            onClick={() => {
                                                if (
                                                    cellIndex === 0 ||
                                                    (cellIndex === 1 && window.innerWidth < 768)
                                                ) {
                                                    toggleRow(globalIndex);
                                                }
                                            }}
                                        >
                                            {cell.render("Cell")}
                                        </Td>
                                    ))}
                                </Tr>

                                {selectedRow === globalIndex && window.innerWidth < 768 && (
                                    <Tr>
                                        <Td colSpan={columns.length}>
                                            <ul className="flex flex-col gap-2">
                                                <li>
                                                    <strong>For</strong> {tableData[globalIndex]?.for}
                                                </li>
                                                <li>
                                                    <strong>Move Out Reason</strong>{" "}
                                                    {tableData[globalIndex]?.moveOutReason}
                                                </li>
                                                <li>
                                                    <strong>Description</strong>{" "}
                                                    {tableData[globalIndex]?.description}
                                                </li>
                                                <li>
                                                    <strong>Action</strong>{" "}
                                                    {tableData[globalIndex]?.action}
                                                </li>
                                            </ul>
                                        </Td>
                                    </Tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </Tbody>
            </CustomTable>
            {apiData.length > 0 && (
                <>
                    {/* {filteredApiData.length > 0 ? ( */}
                    <PaginationWrapper >
                        <div className="px-2">
                            Page <em>{pageIndex + 1} of {pageOptions.length}</em>
                        </div>
                        <div className="flex gap-1">
                            <PrevBtn onClick={() => previousPage()} disabled={!canPreviousPage}>
                                <img src={Images.Arrow} alt="arrow" className="w-2 opacity-75 " />
                            </PrevBtn>
                            <NextBtn onClick={() => nextPage()} disabled={!canNextPage}>
                                <img src={Images.Arrow} alt="arrow" className="w-2 rotate-180 opacity-75" />
                            </NextBtn>
                        </div>
                    </PaginationWrapper>
                    {/* ) : (

                        <div className="text-center py-8 text-gray-500">No move out requests found</div>
                    )} */}
                </>)
            }
        </>
    );
};

const CustomTable = tw.table` w-full text-zinc-600`;
const Thead = tw.thead`border-b uppercase text-gray-600 bg-blue-50`;
const Tbody = tw.tbody`${(props) => (props.hasdata === "true" ? "border-b" : "")}`;
const Tr = tw.tr`${(props) => (props.hasdata === "true" ? "border-b" : "")} rounded-md overflow-hidden hover:bg-slate-50`;
const Th = tw.th`text-left text-sm md:p-3 p-0.5 font-normal text-gray-600`;
const Td = tw.td`p-3 text-sm md:p-3 text-left`;
const UserImageWrapper = tw.div` flex items-center gap-2 cursor-pointer`;
const UserImage = tw.img`w-10 h-10 rounded-full `;

export default Table;
